import ApiCaller from '../lib/ApiCaller';
import MYDocument, { MYDocumentStatus } from '../lib/common/models/myDocument';
import MYDocumentCollection from '../lib/common/models/myDocumentCollection';
import MYDocumentStatusLogCollection from 'lib/common/models/myDocumentStatusLogCollection';
import MYDocumentStatusLog from 'lib/common/models/myDocumentStatusLog';

class ExamDocApi {
  apiCaller: ApiCaller;
  constructor(apiCaller: ApiCaller) {
    this.apiCaller = apiCaller;
  }

  getCertificatesRaw(user: string): Promise<void | any | undefined> {
    return this.apiCaller.call(`/v1/documents/document/${user}`, 'GET');
  }

  getDraftRaw(user: string): Promise<void | any | undefined> {
    return this.apiCaller.call(`/v1/documents/draft/${user}`, 'GET');
  }
  removeDraft(id: string): Promise<void | any | undefined> {
    return this.apiCaller.call(`/v1/exam/removedraft/${id}`, 'DELETE');
  }

  getDocsRaw(country: string): Promise<void | any | undefined> {
    return this.apiCaller
      .call(`/v1/exam/getAllModels/${country}`, 'GET')
      .then((data) => {
        return {
          models: data.models.map((m: any) => {
            return m;
          }),
        };
      });
  }

  search(term: string, country?: string): Promise<void | any | undefined> {
    return this.apiCaller
      .call(`/v1/exam/search/${term}/${country}`, 'GET')
      .then((data) => {
        return {
          models: data.models.map((m: any) => {
            return m;
          }),
        };
      });
  }

  getModelBySlug(slug: string): Promise<any> {
    return this.apiCaller
      .call(`/v1/exam/getModelBySlug/${slug}`, 'GET')
      .then((data) => {
        data.examContent.vars = data.examContent.vars.map((v: string) => {
          return { key: v, value: '' };
        });
        return data;
      });
  }

  /**
   * @deprecated not used, requires replacing slug by MYDocument id
   */
  sendToWriteDoc(
    content: string,
    slug: string,
  ): Promise<void | any | undefined> {
    return this.apiCaller
      .download('/v1/exam/sendToWriteDoc', 'POST', {
        base: content,
        slug: slug,
      })
      .then((file) => {
        return file;
      });
  }

  async sendToWritePDF(
    documentId: string,
    content?: string,
  ): Promise<void | any | undefined> {
    return this.apiCaller
      .download('/v1/exam/sendToWritePDF', 'POST', {
        base: content,
        documentId,
      })
      .then((file) => {
        return file;
      });
  }

  createPDFWithCertificate(
    content: string,
    documentId: string
  ): Promise<void | any | undefined> {
    return this.apiCaller
      .call('/v1/exam/createPDFWithCertificate', 'POST', {
        base: content,
        documentId
      })
      .then((file) => {
        return file;
      });
  }

  updateDraft(
    draftId: string,
    content: string,
    slug: string,
    userId: string,
    vars: any[],
    customName?: string,
    customDescription?: string,
    date?: Date,
  ): Promise<void | any | undefined> {
    const params = {
      base: content,
      slug: slug,
      userId: userId,
      customName: customName,
      customDescription: customDescription,
      date: date,
      vars: vars,
    };
    return this.apiCaller
      .call('/v1/exam/createDraft/' + draftId, 'POST', params)
      .then((file) => {
        return file;
      });
  }

  async downloadExamByNavigationExamId(
    navigationExamId: string,
  ): Promise<void | any | undefined> {
    return this.apiCaller
      .download('/v1/exam/download/exam/' + navigationExamId, 'GET')
      .then((file) => {
        return file;
      });
  }

  downloadExamByDocumentDisplayName(displayName: string): Promise<void | any | undefined> {
    return this.apiCaller
      .download('/v1/exam/download/exam/displayName/' + displayName, 'GET')
      .then((file) => {
        return file;
      });
  }

  downloadAnswers(id: string): Promise<void | any | undefined> {
    return this.apiCaller
      .download('/v1/exam/download/answers/' + id, 'GET')
      .then((file) => {
        return file;
      });
  }

  async getExamPoolDrafts(
    moduleId?: string,
    token?: string,
  ): Promise<MYDocumentCollection> {
    const queryParamsString = moduleId ? `?moduleId=${moduleId}` : '';
    return this.apiCaller
      .call(
        `/v1/exam/examPoolDrafts${queryParamsString}`,
        'GET',
        undefined,
        token,
      )
      .then((data) => {
        return new MYDocumentCollection(
          data.documents.map((doc: any) => new MYDocument(doc)),
        );
      })
      .catch((err) => {
        console.error(err);
        throw Error(err);
      });
  }

  async changeExamPoolDraftStatus(
    draftId: string,
    status: MYDocumentStatus,
    token?: string,
  ): Promise<MYDocument> {
    return this.apiCaller
      .call(
        `/v1/exam/examPoolDraft/${draftId}/changeStatus`,
        'PUT',
        { status },
        token,
      )
      .then((data) => {
        if (data.message) {
          throw new Error(data.message);
        }
        return new MYDocument(data);
      });
  }

  async createExamPoolDraft(
    syllabusId: string,
    moduleId: string,
    token?: string,
  ): Promise<MYDocument> {
    return this.apiCaller
      .call(
        '/v1/exam/examPoolDraft',
        'POST',
        { syllabusId, moduleId },
        token,
      )
      .then((data) => {
        if (data.message) {
          throw new Error(data.message);
        }
        return new MYDocument(data);
      });
  }

  async deleteExamPoolDraft(draftId: string, token?: string): Promise<void> {
    return this.apiCaller
      .call(`/v1/exam/examPoolDraft/${draftId}`, 'DELETE', undefined, token)
      .then((data) => {
        if (data.message) {
          throw new Error(data.message);
        }
        return;
      });
  }

  /**
   * Load a list of documents by statuses
   * @param statuses 
   * @param token 
   * @returns 
   */
  async loadDraftExamsByStatuses(statuses: MYDocumentStatus[], token?: string): Promise<MYDocumentCollection> {
    const queryParamsString = statuses.map(status => `statuses=${status}`).join('&');
    return this.apiCaller
      .call(`/v1/exam/loadDraftExamsByStatuses?${queryParamsString}`, 'GET', undefined, token)
      .then((data) => {
        return new MYDocumentCollection(
          data.documents.map((doc: any) => new MYDocument(doc)),
        );
      })
      .catch((err) => {
        console.error(err);
        throw Error(err);
      });
  }

  /**
   * Refuse a document
   * @param documentId
   * @param reason
   * @param token
   * @returns
   */
  async refuseMCADocument(documentId: string, reason:string,  token?: string): Promise<void> {
    return this.apiCaller
      .call(`/v1/exam/document/${documentId}/refuseMCA`, 'POST', { reason }, token)
      .then((data) => {
        if (data.message) {
          throw new Error(data.message);
        }
        return;
      });
  }

  /**
   * Approve a document
   * @param documentId 
   * @param token 
   * @returns 
   */
  async approveMCADocument(documentId: string, token?: string): Promise<void> {
    return this.apiCaller
      .call(`/v1/exam/document/${documentId}/approveMCA`, 'POST', undefined, token)
      .then((data) => {
        if (data.message) {
          throw new Error(data.message);
        }
        return;
      });
  }

  /**
   * Load all status logs of a document
   * @param documentId 
   * @returns 
   */
    async loadAllStatusLogs(
      documentId:string,
      token?  : string
    ): Promise<MYDocumentStatusLogCollection> {
      return this.apiCaller.call('/v1/exam/document/'+documentId+'/statusLogs', 'GET',undefined,token)
        .then((data) => {
          const dArr = data.data.map((doc: any) => new MYDocumentStatusLog(doc));
          return new MYDocumentStatusLogCollection(dArr);
        })
        .catch((err) => {
          console.error(err);
          throw Error(err);
        });
    }

  async loadAllStatusLogsByNavigationExamId(
    navigationExamId: string,
    token?: string
  ): Promise<MYDocumentStatusLogCollection> {
    return this.apiCaller
      .call('/v1/exam/document/byNavigationExam/' + navigationExamId + '/statusLogs', 'GET', undefined, token)
      .then((data) => {
        const dArr = data.data.map((doc: any) => new MYDocumentStatusLog(doc));
        return new MYDocumentStatusLogCollection(dArr);
      })
      .catch((err) => {
        console.error(err);
        throw Error(err);
      });
  }
}

export default ExamDocApi;
